import React, { useEffect, useMemo, useRef, useState } from "react";
import { Move, RotateCcw, RotateCw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  buildPreviewFilter,
  clamp,
  coverCrop,
  cropZoom,
  effectiveImageSize,
  initialReportCrop,
  type ReportReadyCropPlacement,
  type ReportReadyEditorAdjustments,
  type ReportReadyImagePreset,
  type ReportReadySavedImage,
} from "./report-ready-editor-utils";
import "./ReportReadyImageEditor.css";

interface ReportReadyImageEditorProps {
  imageUrl: string;
  initialAdjustments?: Partial<ReportReadyEditorAdjustments>;
  reportCrop?: ReportReadyCropPlacement | null;
  saving?: boolean;
  onSave: (image: ReportReadySavedImage) => void | Promise<void>;
}

const DEFAULT_ADJUSTMENTS: ReportReadyEditorAdjustments = {
  rotation: 0,
  brightness: 0,
  contrast: 0,
  preset: "none",
};

export type { ReportReadyCropPlacement, ReportReadySavedImage } from "./report-ready-editor-utils";

export function ReportReadyImageEditor({
  imageUrl,
  initialAdjustments,
  reportCrop,
  saving = false,
  onSave,
}: ReportReadyImageEditorProps) {
  const [loadError, setLoadError] = useState<string | null>(null);
  const [imageSource, setImageSource] = useState<string | null>(null);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [adjustments, setAdjustments] = useState<ReportReadyEditorAdjustments>({
    ...DEFAULT_ADJUSTMENTS,
    ...initialAdjustments,
  });
  const [crop, setCrop] = useState<ReportReadyCropPlacement | null>(reportCrop ?? null);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ pointerId: number; x: number; y: number; crop: ReportReadyCropPlacement } | null>(null);
  const previousRotationRef = useRef(adjustments.rotation);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    setImageSource(null);
    setLoadError(null);
    setNaturalSize({ width: 0, height: 0 });
    setAdjustments({ ...DEFAULT_ADJUSTMENTS, ...initialAdjustments });
    setCrop(reportCrop ?? null);

    void (async () => {
      try {
        const response = await fetch(imageUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setImageSource(objectUrl);
      } catch {
        if (!cancelled) setLoadError("The source image could not be loaded for editing.");
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [imageUrl, initialAdjustments, reportCrop]);

  const imageSize = useMemo(
    () => effectiveImageSize(naturalSize.width, naturalSize.height, adjustments.rotation),
    [adjustments.rotation, naturalSize.height, naturalSize.width],
  );

  const baseCrop = useMemo(
    () => imageSize.width && imageSize.height ? coverCrop(imageSize.width, imageSize.height) : null,
    [imageSize],
  );

  const zoom = crop && baseCrop ? cropZoom(crop, baseCrop) : 1;
  const previewFilter = buildPreviewFilter(adjustments.brightness, adjustments.contrast, adjustments.preset);

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const nextNaturalSize = {
      width: event.currentTarget.naturalWidth,
      height: event.currentTarget.naturalHeight,
    };
    setNaturalSize(nextNaturalSize);
    setCrop((current) => {
      if (current) return current;
      const effective = effectiveImageSize(
        nextNaturalSize.width,
        nextNaturalSize.height,
        adjustments.rotation,
      );
      return initialReportCrop(effective.width, effective.height);
    });
  };

  useEffect(() => {
    if (!naturalSize.width || !naturalSize.height) return;
    if (previousRotationRef.current === adjustments.rotation) return;
    previousRotationRef.current = adjustments.rotation;
    setCrop(initialReportCrop(imageSize.width, imageSize.height));
  }, [adjustments.rotation, imageSize.height, imageSize.width, naturalSize.height, naturalSize.width]);

  const changeZoom = (value: number[]) => {
    const nextZoom = value[0] ?? 1;
    if (!baseCrop) return;
    setCrop((current) => {
      const active = current ?? baseCrop;
      const centerX = active.x + active.width / 2;
      const centerY = active.y + active.height / 2;
      const width = baseCrop.width / nextZoom;
      const height = baseCrop.height / nextZoom;
      return {
        x: clamp(centerX - width / 2, 0, 1 - width),
        y: clamp(centerY - height / 2, 0, 1 - height),
        width,
        height,
      };
    });
  };

  const beginDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!crop) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, crop };
  };

  const dragImage = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const frame = frameRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !frame) return;
    const bounds = frame.getBoundingClientRect();
    const deltaX = event.clientX - drag.x;
    const deltaY = event.clientY - drag.y;
    const nextCrop = {
      ...drag.crop,
      x: clamp(drag.crop.x - (deltaX / bounds.width) * drag.crop.width, 0, 1 - drag.crop.width),
      y: clamp(drag.crop.y - (deltaY / bounds.height) * drag.crop.height, 0, 1 - drag.crop.height),
    };
    setCrop(nextCrop);
    dragRef.current = {
      pointerId: drag.pointerId,
      x: event.clientX,
      y: event.clientY,
      crop: nextCrop,
    };
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
  };

  const rotateBy = (delta: number) => {
    setAdjustments((current) => ({
      ...current,
      rotation: (current.rotation + delta + 360) % 360,
    }));
  };

  const setPreset = (preset: ReportReadyImagePreset) => {
    setAdjustments((current) => ({ ...current, preset }));
  };

  const saveReportReadyImage = async () => {
    if (!crop) return;
    await onSave({ reportCrop: crop, adjustments });
  };

  if (loadError) {
    return (
      <div className="flex min-h-[520px] items-center justify-center rounded-md border bg-muted px-6 text-center text-sm text-destructive">
        {loadError}
      </div>
    );
  }

  if (!imageSource) {
    return (
      <div className="flex min-h-[520px] items-center justify-center rounded-md border bg-muted text-sm text-muted-foreground">
        Loading image editor...
      </div>
    );
  }

  return (
    <div className="report-ready-image-editor">
      <div className="report-ready-editor-body">
        <aside className="report-ready-adjustments-panel">
          <div>
            <h3>Image adjustments</h3>
            <p>Corrections render from the full-resolution original when you save.</p>
          </div>

          <div className="report-ready-adjustment-group">
            <div className="report-ready-adjustment-label">
              <span>Brightness</span>
              <strong>{adjustments.brightness >= 0 ? "+" : ""}{adjustments.brightness.toFixed(2)}</strong>
            </div>
            <Slider
              aria-label="Brightness"
              min={-1}
              max={1}
              step={0.05}
              value={[adjustments.brightness]}
              onValueChange={(value) => setAdjustments((current) => ({ ...current, brightness: value[0] ?? 0 }))}
            />
          </div>

          <div className="report-ready-adjustment-group">
            <div className="report-ready-adjustment-label">
              <span>Contrast</span>
              <strong>{adjustments.contrast >= 0 ? "+" : ""}{adjustments.contrast.toFixed(2)}</strong>
            </div>
            <Slider
              aria-label="Contrast"
              min={-1}
              max={1}
              step={0.05}
              value={[adjustments.contrast]}
              onValueChange={(value) => setAdjustments((current) => ({ ...current, contrast: value[0] ?? 0 }))}
            />
          </div>

          <div className="report-ready-adjustment-group">
            <div className="report-ready-adjustment-label">
              <span>Rotate</span>
              <strong>{adjustments.rotation}°</strong>
            </div>
            <div className="report-ready-rotate-buttons">
              <Button type="button" variant="outline" size="sm" onClick={() => rotateBy(-90)}>
                <RotateCcw className="mr-2 h-4 w-4" />
                Left
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => rotateBy(90)}>
                <RotateCw className="mr-2 h-4 w-4" />
                Right
              </Button>
            </div>
          </div>

          <div className="report-ready-adjustment-group">
            <div className="report-ready-adjustment-label">
              <span>Preset</span>
            </div>
            <div className="report-ready-preset-buttons">
              {(["none", "grayscale", "sepia"] as const).map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={adjustments.preset === preset ? "is-active" : undefined}
                  onClick={() => setPreset(preset)}
                >
                  {preset === "none" ? "Color" : preset === "grayscale" ? "B&W" : "Sepia"}
                </button>
              ))}
            </div>
          </div>
        </aside>

        <div className="report-ready-preview-panel">
          <div className="report-ready-frame-copy">
            <h3>Choose how the image will appear in the report</h3>
            <p>Drag to reposition or use the slider to zoom. The full image remains available in PowerPoint.</p>
          </div>

          <div className="report-ready-frame-workspace">
            <div
              ref={frameRef}
              className="report-ready-frame"
              onPointerDown={beginDrag}
              onPointerMove={dragImage}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            >
              <img
                src={imageSource}
                alt="Report frame preview"
                draggable={false}
                onLoad={handleImageLoad}
                style={crop ? {
                  width: `${100 / crop.width}%`,
                  height: `${100 / crop.height}%`,
                  left: `${-(crop.x / crop.width) * 100}%`,
                  top: `${-(crop.y / crop.height) * 100}%`,
                  filter: previewFilter,
                  transform: `rotate(${adjustments.rotation}deg)`,
                } : { opacity: 0 }}
              />
              <div className="report-ready-frame-outline" aria-hidden="true" />
              <div className="report-ready-frame-drag-hint">
                <Move className="h-4 w-4" />
                Drag to reposition
              </div>
            </div>
          </div>

          <div className="report-ready-frame-controls">
            <div className="report-ready-frame-zoom-label">
              <Search className="h-4 w-4" />
              <span>Image zoom</span>
              <strong>{zoom.toFixed(1)}x</strong>
            </div>
            <Slider aria-label="Image zoom" min={1} max={4} step={0.1} value={[zoom]} onValueChange={changeZoom} />
          </div>
        </div>
      </div>

      <div className="report-ready-editor-footer">
        <span>Adjust the image and position it in one step. Saving renders the full-resolution version on the server.</span>
        <Button type="button" onClick={() => void saveReportReadyImage()} disabled={saving || !crop}>
          {saving ? "Saving..." : "Save Report Ready image"}
        </Button>
      </div>
    </div>
  );
}
